import React from "react";
import { Box, Paper, Typography, ToggleButton, ToggleButtonGroup, IconButton, Tooltip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { ChartContainer } from "@mui/x-charts/ChartContainer";
import { BarPlot } from "@mui/x-charts/BarChart";
import { LinePlot, MarkPlot } from "@mui/x-charts/LineChart";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import { ChartsLegend } from "@mui/x-charts/ChartsLegend";
import api from "../Auth/Api";
import globals from "../Globals";
import { subscribePoll } from "../Layout/Poller";
import { PageContainer, SectionHeader, StatCard, SelectField, relTime } from "../Layout/Common";

// The title's dashboard (players stats): who is online right now, the daily/weekly/monthly actives, new players
// and logins against the same window a day and a week earlier, and the last hours as charts. Live figures are
// counted on the players collection, the history is what the backend rolls up per hour (PlayerStats).

const REFRESH_MS = 60000;
const RANGES = [{ value: 24, label: "24h" }, { value: 48, label: "48h" }, { value: 168, label: "7d" }];
const BRANCH_KEY = "home.branch";

const num = (v) => v == null ? "—" : Number(v).toLocaleString();

// "+7.29%" against the compared window, PlayFab style; "NA" when there is nothing to compare with
function delta(now, before) {
    if (!before) return { text: "NA", color: undefined };
    const pct = (now - before) / before * 100;
    return { text: `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`, color: pct >= 0 ? "success.main" : "error.main" };
}

export default class HomePage extends React.Component {
    state = { branch: localStorage.getItem(BRANCH_KEY) || "Production", hours: 48, data: null, loadedAt: null, error: null };

    componentDidMount() {
        this.load();
        this.unsub = subscribePoll(this.load, REFRESH_MS);
    }

    componentWillUnmount() { if (this.unsub) this.unsub(); }

    load = () => {
        const { branch, hours } = this.state;
        return api.group("players")("Loading statistics", ["stats"], { payload: { branch, hours }, silent: true })
            .then(r => {
                if (r && r.series) this.setState({ data: r, loadedAt: Date.now(), error: null });
                return true;
            })
            .catch(e => { this.setState({ error: e.message }); return false; });
    }

    setBranch = (branch) => {
        localStorage.setItem(BRANCH_KEY, branch);
        this.setState({ branch, data: null }, this.load);
    }

    timeLabel = (t) => {
        const d = new Date(t);
        const h = `${String(d.getHours()).padStart(2, "0")}:00`;
        return this.state.hours > 24 ? `${d.getMonth() + 1}/${d.getDate()} ${h}` : h;
    }

    renderCards() {
        const d = this.state.data;
        const n1 = delta(d.newPlayers.last24h, d.newPlayers.previous24h), n7 = delta(d.newPlayers.last24h, d.newPlayers.weekAgo);
        const l1 = delta(d.logins.last24h, d.logins.previous24h), l7 = delta(d.logins.last24h, d.logins.weekAgo);
        return (
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                <StatCard label="Online now" value={num(d.onlineNow)} secondary="seen in the last 5 minutes" />
                <StatCard label="Daily active" value={num(d.dau)} secondary={`weekly ${num(d.wau)} · monthly ${num(d.mau)}`} tertiary={`${num(d.total)} players on ${d.branch}`} />
                <StatCard label="New players (24h)" value={num(d.newPlayers.last24h)}
                          secondary={`1 day ago ${num(d.newPlayers.previous24h)} · ${n1.text}`}
                          tertiary={`7 days ago ${num(d.newPlayers.weekAgo)} · ${n7.text}`} tertiaryColor={n7.color} />
                <StatCard label="Logins (24h)" value={num(d.logins.last24h)}
                          secondary={`1 day ago ${num(d.logins.previous24h)} · ${l1.text}`}
                          tertiary={`7 days ago ${num(d.logins.weekAgo)} · ${l7.text}`} tertiaryColor={l7.color} />
            </Box>
        );
    }

    chart(title, series, xLabels, children, height = 240) {
        return (
            <Paper sx={{ p: 2, mb: 2, flex: 1, minWidth: 380 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>{title}</Typography>
                {xLabels.length === 0
                    ? <Typography variant="body2" color="text.secondary">Nothing in this range yet.</Typography>
                    : <ChartContainer height={height}
                                      xAxis={[{ id: "t", scaleType: "band", data: xLabels, tickLabelStyle: { fontSize: 10 },
                                                tickInterval: (v, i) => i % Math.max(1, Math.round(xLabels.length / 8)) === 0 }]}
                                      yAxis={[{ id: "n", min: 0, valueFormatter: (v) => Number.isInteger(v) ? String(v) : "" }]} series={series}>
                        {children}
                        <ChartsXAxis axisId="t" />
                        <ChartsYAxis axisId="n" />
                        <ChartsTooltip />
                        <ChartsLegend />
                    </ChartContainer>}
            </Paper>
        );
    }

    renderCharts() {
        const s = this.state.data.series;
        const x = s.map(r => this.timeLabel(r.t));
        const sampled = s.some(r => r.onlinePeak > 0);
        return (
            <Box>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {this.chart("Online players (per hour: average and peak of the 5-minute samples)", [
                        { type: "line", id: "avg", label: "average", data: s.map(r => r.online), yAxisId: "n", showMark: false },
                        { type: "line", id: "peak", label: "peak", data: s.map(r => r.onlinePeak), yAxisId: "n", showMark: false },
                    ], x, <><LinePlot /><MarkPlot /></>)}
                    {this.chart("Logins per hour", [
                        { type: "bar", id: "logins", label: "logins", data: s.map(r => r.logins), yAxisId: "n" },
                    ], x, <BarPlot />)}
                </Box>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {this.chart("New players per hour", [
                        { type: "bar", id: "new", label: "new players", data: s.map(r => r.newPlayers), yAxisId: "n" },
                    ], x, <BarPlot />)}
                </Box>
                {!sampled && <Typography variant="body2" color="text.secondary">Online history fills in as the scheduler samples the players online every 5 minutes.</Typography>}
            </Box>
        );
    }

    render() {
        const { data, loadedAt, error, branch, hours } = this.state;
        return (
            <PageContainer maxWidth={1400}>
                <SectionHeader title={`${globals.loginData?.titles?.titles?.[globals.selectedTitle]?.name ?? globals.selectedTitle} — overview`}>
                    {loadedAt && <Typography variant="caption" color="text.secondary">updated {relTime(loadedAt)}</Typography>}
                    <SelectField label="Branch" size="small" minWidth={150} value={branch} options={globals.branches} onChange={this.setBranch} />
                    <ToggleButtonGroup size="small" exclusive value={hours} onChange={(e, v) => v && this.setState({ hours: v, data: null }, this.load)}>
                        {RANGES.map(r => <ToggleButton key={r.value} value={r.value}>{r.label}</ToggleButton>)}
                    </ToggleButtonGroup>
                    <Tooltip title="Refresh"><IconButton size="small" onClick={this.load}><RefreshIcon /></IconButton></Tooltip>
                </SectionHeader>
                {error && !data && <Typography color="error">Could not load the statistics: {error}</Typography>}
                {!data && !error && <Typography variant="body2" color="text.secondary">Loading…</Typography>}
                {data && this.renderCards()}
                {data && this.renderCharts()}
            </PageContainer>
        );
    }
}
